import { useState, useEffect } from "react";
import { Helmet } from 'react-helmet-async';
import { Box, Spinner, Alert, AlertIcon, Container } from "@chakra-ui/react";
import ServiceDetailsHeader from "../components/Common/ImageHeader";
import OfficeLocation from "../components/Contact/OfficeLocation";
import ContactForm from "../components/Contact/ContactForm";
import ErrorBoundary from "../components/ErrorBoundary";
import { contactService, formatContactData } from "../services/contactService";
import { useNavigate, useSearchParams } from "react-router-dom";

const Contact = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [contactData, setContactData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get form type from URL parameters
  const formType = searchParams.get('form');

  useEffect(() => {
    fetchContactData();
  }, []);

  const fetchContactData = async () => {
    try {
      setLoading(true);
      setError(null); // Clear previous errors
      
      const response = await contactService.getContactData();

      if (response.success && response.data) {
        const formattedData = formatContactData(response.data);
        setContactData(formattedData);

        // If there's an error message but success is true (permission issue)
        if (response.error) {
          console.warn('Warning:', response.error);
        }
      } else {
        console.error('API response failed:', response);
        setError(response.error || 'No data received from API');
      }
    } catch (err) {
      console.error("Error in fetchContactData:", err);
      setError("Failed to load contact data");
    } finally {
      setLoading(false);
    }
  };

  const handleBreadcrumbClick = (href) => {
    if (href && href !== "#") {
      navigate(href);
    }
  };

  if (loading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minH="400px"
        >
          <Spinner size="xl" color="#000B8C" thickness="4px" />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxW="container.xl" py={8}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        {/* Basic Meta Tags */}
        <title>Contact Us | Business Automation Ltd. | Trusted Tech Partner in Bangladesh</title>
        <meta 
          name="description" 
          content="Get in touch with Business Automation Ltd. for digital government solutions, enterprise tech, AI-powered platforms, and outsourcing partnerships in Bangladesh." 
        />
        <meta 
          name="keywords" 
          content="Contact Business Automation Ltd, IT solution provider Bangladesh contact, eGovernment solutions contact Bangladesh, AI software company Bangladesh, Business Automation contact number, Government IT project partner Bangladesh, Managed service provider Bangladesh, Tech company contact Dhaka, ITES company Bangladesh, Software development company Bangladesh, Software outsourcing Bangladesh contact, IT job application Bangladesh, Contact RPA provider Bangladesh, Internship inquiry Business Automation, Enterprise technology partner Bangladesh" 
        />
        <link rel="canonical" href="https://www.ba-systems.com/contact" />
        <meta name="robots" content="index, follow" />

        {/* Open Graph / Facebook */}
        <meta property="og:url" content="https://ba-systems.com/contact" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Contact Us | Business Automation Ltd. | Trusted Tech Partner in Bangladesh" />
        <meta property="og:description" content="Get in touch with Business Automation Ltd. for digital government solutions, enterprise tech, AI-powered platforms, and outsourcing partnerships in Bangladesh." />
        <meta property="og:image" content="https://opengraph.b-cdn.net/production/images/e1930f8a-3aa2-4fe1-b6ea-0ad5030ebc97.png?token=vTkCXbq7qBnee_uxOFTj3-OKo7vP2KVzX66L6BYKzho&height=596&width=1200&expires=33297038875" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="twitter:domain" content="ba-systems.com" />
        <meta property="twitter:url" content="https://ba-systems.com/contact" />
        <meta name="twitter:title" content="Contact Us | Business Automation Ltd. | Trusted Tech Partner in Bangladesh" />
        <meta name="twitter:description" content="Get in touch with Business Automation Ltd. for digital government solutions, enterprise tech, AI-powered platforms, and outsourcing partnerships in Bangladesh." />
        <meta name="twitter:image" content="https://opengraph.b-cdn.net/production/images/e1930f8a-3aa2-4fe1-b6ea-0ad5030ebc97.png?token=vTkCXbq7qBnee_uxOFTj3-OKo7vP2KVzX66L6BYKzho&height=596&width=1200&expires=33297038875" />
      </Helmet>
      
      <ErrorBoundary>
      <ServiceDetailsHeader
        title={contactData?.contactHeader?.title}
        subtitle={contactData?.contactHeader?.description}
        backgroundImage={contactData?.contactHeader?.backgroundImage}
        breadcrumbItems={[
          { label: "Home", href: "/" },
          { label: "Contact Us", href: "#", isCurrentPage: true },
        ]}
        onBreadcrumbClick={handleBreadcrumbClick}
      />

      {/* <ConnectSection data={contactData?.letsConnect} /> */}
      <Box
        backgroundImage="url('/png/background.png')"
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundRepeat="no-repeat"
        minHeight="100vh"
        position="relative"
      >
        <ContactForm 
          data={contactData?.getinTouch} 
          initialTab={formType || 'product'} 
        />
        <OfficeLocation data={contactData?.officeLocation} />
      </Box>

      {/* <ProductInquiry 
        data={contactData?.productInquiry} 
        officeData={contactData?.officeAddress} 
      />
      <SupportPortal data={contactData?.supportPortal} />
      <ClientSection data={contactData?.clientSection} />
      <PartnerPortal data={contactData?.partnerPortal} /> */}
    </ErrorBoundary>
    </>
  );
};

export default Contact;
